import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { MessageSquare, Send, Phone, ArrowLeft, User } from "lucide-react";
import { toast } from "sonner";
import { MessageMedia } from "@/components/messages/MessageMedia";

const QUICK_REPLIES = [
  "On my way — see you shortly.",
  "Running a little late, thanks for your patience.",
  "Your part has arrived — let's schedule your repair.",
  "We're here at your address.",
  "Can we reschedule your appointment?",
];

const STATUS_LABELS = {
  queued: "Sending…", accepted: "Sending…", scheduled: "Scheduled", sending: "Sending…",
  sent: "Sent", delivered: "Delivered", read: "Read",
  undelivered: "Not delivered", failed: "Failed",
};
function statusLabel(s) {
  if (!s) return "Sending…";
  return STATUS_LABELS[s] || s;
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
        " " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const activeId = params.get("customer") || "";
  const [threads, setThreads] = useState([]);
  const [convo, setConvo] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setThreads(data);
    } catch (e) { /* silent poll */ }
  }, []);

  const loadConvo = useCallback(async (id) => {
    if (!id) { setConvo(null); return; }
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setConvo(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await api.post(`/conversations/${id}/read`);
      window.dispatchEvent(new Event("conversations-updated"));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 10000); return () => clearInterval(t); }, [loadThreads]);

  useEffect(() => {
    if (!activeId) { setConvo(null); return; }
    loadConvo(activeId);
    markRead(activeId).then(loadThreads);
    const t = setInterval(() => loadConvo(activeId), 5000);
    return () => clearInterval(t);
  }, [activeId, loadConvo, markRead, loadThreads]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [convo?.messages?.length]);

  const openThread = (id) => setParams({ customer: id });
  const backToList = () => setParams({});

  const send = async (bodyText) => {
    const body = (bodyText ?? text).trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      await api.post(`/conversations/${activeId}/send`, { body });
      setText("");
      await loadConvo(activeId);
      await loadThreads();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="messages-root">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Messages</h1>
        <p className="text-sm text-slate-500 mt-1">Two-way text conversations with your customers.</p>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="grid md:grid-cols-[320px_1fr] auto-rows-[minmax(0,1fr)] h-[calc(100dvh-240px)] md:h-[70vh] min-h-[380px] md:min-h-[480px]">
          {/* Thread list */}
          <div className={`border-r border-slate-200 overflow-y-auto min-h-0 ${activeId ? "hidden md:block" : "block"}`} data-testid="thread-list">
            {threads.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                No conversations yet. Replies from customers will appear here.
              </div>
            )}
            {threads.map((t) => (
              <button
                key={t.customer_id}
                onClick={() => openThread(t.customer_id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${activeId === t.customer_id ? "bg-blue-50" : ""}`}
                data-testid={`thread-${t.customer_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900 truncate">{t.customer_name}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{fmtTime(t.last_at)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-slate-500 truncate">
                    {t.last_direction === "outbound" ? "You: " : ""}{t.last_body || "[Photo]"}
                  </span>
                  {t.unread > 0 && (
                    <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center" data-testid={`thread-unread-${t.customer_id}`}>
                      {t.unread}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Conversation */}
          <div className={`flex-col min-h-0 overflow-hidden ${activeId ? "flex" : "hidden md:flex"}`}>
            {!activeId && (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
                Select a conversation to view messages.
              </div>
            )}
            {activeId && convo && (
              <>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white">
                  <Button variant="ghost" size="sm" className="md:hidden px-2" onClick={backToList} data-testid="convo-back">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                    <User className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate" data-testid="convo-name">
                      {convo.customer.first_name} {convo.customer.last_name}
                    </div>
                    <div className="text-xs text-slate-500">{convo.customer.phone}</div>
                  </div>
                  {convo.customer.phone && (
                    <a href={`tel:${convo.customer.phone}`} className="ml-auto" data-testid="convo-call">
                      <Button variant="outline" size="icon" className="rounded-full"><Phone className="w-4 h-4" /></Button>
                    </a>
                  )}
                </div>

                <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3 bg-slate-50" data-testid="convo-messages">
                  {convo.messages.length === 0 && (
                    <div className="text-center text-sm text-slate-400 mt-8">No messages yet. Say hello 👋</div>
                  )}
                  {convo.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${m.direction === "outbound" ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"}`}>
                        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                        <MessageMedia message={m} customerId={activeId} />
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${m.direction === "outbound" ? "text-blue-100 justify-end" : "text-slate-400"}`}>
                          <span>{fmtTime(m.created_at)}</span>
                          {m.direction === "outbound" && m.source && m.source !== "manual" && <span>· Auto</span>}
                          {m.direction === "outbound" && <span>· {statusLabel(m.status)}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick replies */}
                <div className="px-3 pt-2 flex gap-2 overflow-x-auto border-t border-slate-100 bg-white">
                  {QUICK_REPLIES.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => send(q)}
                      disabled={sending}
                      className="shrink-0 text-xs border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-50 hover:border-blue-300 hover:text-blue-700 transition-colors disabled:opacity-50"
                      data-testid={`quick-reply-${i}`}
                    >
                      {q.length > 26 ? q.slice(0, 26) + "…" : q}
                    </button>
                  ))}
                </div>

                {/* Composer */}
                <form
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                  className="flex items-center gap-2 p-3 border-t border-slate-200 bg-white"
                >
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message…"
                    disabled={sending}
                    data-testid="convo-input"
                  />
                  <Button type="submit" disabled={sending || !text.trim()} className="bg-blue-700 hover:bg-blue-800 shrink-0" data-testid="convo-send">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
